import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable/DataTable";
import { Job } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

const PercentageDifference = ({ value }) => {
  if (value === "" || value === null || value === undefined) return null;

  const positiveDifference = value >= 0;

  const valueMap = {
    className: positiveDifference ? "success" : "error",
    sign: positiveDifference ? "+" : "",
  };

  return (
    <MDTypography color={valueMap.className} component="span" variant="body2">
      {valueMap.sign} {value}%
    </MDTypography>
  );
};

const toNumber = (value?: number | string | null) => Number(value ?? 0);

const renderTextCell = (value: string, isTotal = false) => (
  <MDTypography variant="button" fontWeight={isTotal ? "medium" : "regular"}>
    {value}
  </MDTypography>
);

const renderCurrencyCell = (value: number | string, isTotal = false) => (
  <MDTypography variant="button" fontWeight={isTotal ? "medium" : "regular"}>
    {formatCentsToUSD(value)}
  </MDTypography>
);

export default function DirectCostsSummarySection({ job }: { job: Job }) {
  const columns = [
    {
      Header: "Phase",
      accessor: "name",
      width: "20%",
      Cell: (props) => renderTextCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "",
      accessor: "type",
      width: "20%",
      Cell: (props) => renderTextCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Material",
      accessor: "materialCost",
      width: "20%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Equipment",
      accessor: "equipmentCost",
      width: "20%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Labor",
      accessor: "labourCost",
      width: "10%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Other",
      accessor: "additionalCost",
      width: "10%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Transfer Trucks",
      accessor: "transferTrucksCost",
      width: "10%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Total",
      accessor: "totalCost",
      width: "10%",
      align: "right",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "% Difference",
      accessor: "percentageDifference",
      width: "10%",
      align: "right",
      Cell: ({ value }) => <PercentageDifference value={value} />,
    },
  ];

  // TODO: add transfer trucks cost
  const buildTableRows = () => {
    const tableRows = [];
    job.jobPhases.forEach((phase) => {
      tableRows.push({
        name: phase.proposalProduct.material.name,
        type: "Proposed",
        materialCost: phase.proposedCostMaterial,
        equipmentCost: phase.proposedCostEquipment,
        labourCost: phase.proposedCostLabour,
        additionalCost: phase.proposedCostAdditional,
        transferTrucksCost: phase.proposedCostTransferTruck,
        totalCost: phase.proposedCostTotal,
        percentageDifference: "",
      });
      tableRows.push({
        name: "",
        type: "Actuals",
        materialCost: phase.actualCostMaterial,
        equipmentCost: phase.actualCostEquipment,
        labourCost: phase.actualCostLabour,
        additionalCost: phase.actualCostAdditional,
        transferTrucksCost: phase.actualCostTransferTruck,
        totalCost: phase.actualCostTotal,
        percentageDifference:
          toNumber(phase.proposedCostTotal) > 0
            ? Math.round(
                ((toNumber(phase.proposedCostTotal) - toNumber(phase.actualCostTotal)) /
                  toNumber(phase.proposedCostTotal)) *
                  100
              )
            : "",
      });
    });

    tableRows.push({
      name: "Total Job",
      type: "Proposed",
      materialCost: toNumber(job.proposedCostMaterial),
      equipmentCost: toNumber(job.proposedCostEquipment),
      labourCost: toNumber(job.proposedCostLabour),
      additionalCost: toNumber(job.proposedCostAdditional),
      transferTrucksCost: toNumber(job.proposedCostTransferTruck),
      totalCost: toNumber(job.proposedCostTotal),
      percentageDifference: "",
      isTotal: true,
    });
    tableRows.push({
      name: "",
      type: "Actuals",
      materialCost: toNumber(job.actualCostMaterial),
      equipmentCost: toNumber(job.actualCostEquipment),
      labourCost: toNumber(job.actualCostLabour),
      additionalCost: toNumber(job.actualCostAdditional),
      transferTrucksCost: toNumber(job.actualCostTransferTruck),
      totalCost: toNumber(job.actualCostTotal),
      percentageDifference:
        toNumber(job.proposedCostTotal) > 0
          ? Math.round(
              ((toNumber(job.proposedCostTotal) - toNumber(job.actualCostTotal)) /
                toNumber(job.proposedCostTotal)) *
                100
            )
          : "",
      isTotal: true,
    });

    return tableRows;
  };

  const tableData = { columns, rows: buildTableRows() };

  return (
    <Card>
      <MDBox mt={2} p={2}>
        <MDTypography>Direct Costs Summary</MDTypography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <DataTable table={tableData} showTotalEntries={false} compact contentPaddingX={1.5} />
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
